import { EventEmitter, Inject, Injectable, Optional } from '@angular/core';
import { Observable, of } from 'rxjs';

@Injectable()
export class IhmLoadingIndicatorService {
  // Toggle with queue model
  private isShownCounter: number;

  // Toggle with flag model
  private isShown: boolean;

  public onToggle: EventEmitter<boolean>;

  constructor(@Inject('IHM_LOADING_QUEUE_TYPE') @Optional() private readonly isLoadingIndicatorAsQueue: boolean) {
    // The loading indicator should never be shown when the component is initialized.
    this.isShownCounter = 0;
    this.isShown = false;
    this.onToggle = new EventEmitter<boolean>();
  }

  toggle(state: boolean): Observable<boolean> {
    // Set the state based on the value provided to the function.
    const shouldDisplayToggle: boolean = this.applyToggle(state);

    // Emit the event to let component that is listening know that the loading indicator has either been shown or is hidden.
    this.onToggle.emit(shouldDisplayToggle);

    // Return an event in case the user wants to know exactly when it is done, rather than just generally done.
    return of(shouldDisplayToggle);
  }

  removeLoading(): void {
    this.isShownCounter = 0;
    this.isShown = false;
    this.onToggle.emit(false);
  }

  private applyToggle(state: boolean): boolean {
    return this.isLoadingIndicatorAsQueue ? this.applyQueueModelToggle(state) : this.applyFlagModelToggle(state);
  }

  /**
   * Apply the queue model toggle.
   * The idea is to apply the number of true states vs false states.
   * The logic is to always check the numbers of true states.
   *
   * If more true states was passed, more the counter will be increased,
   * and as the false states was passed, it will decrease the number of true states.
   *
   * Which means that, if we pass more false cases, it will decrease until the state counter become 0.
   *
   * Example:
   * true > true > false  | total counter: 1 (result true) Because the third false only canceled one of the true cases.
   * false > true > false | total counter: 0 (result false) Because the second true will be removed by the third param.
   * false > false > true | total counter: 1 (result true) Because the true was the last state passed to it when the counter were 0.
   * true > false > false | total counter: 0 (result false) Because the second false canceled the first true.
   *
   */
  private applyQueueModelToggle(state: boolean): boolean {
    if (state) {
      this.isShownCounter = this.isShownCounter + 1;
    } else {
      this.isShownCounter = this.isShownCounter - 1;
      const isNegativeCounter = this.isShownCounter < 0;

      if (isNegativeCounter) {
        this.isShownCounter = 0;
      }
    }

    return !!this.isShownCounter;
  }

  /**
   * Apply the flag model toggle.
   * The idea is to apply the last state passed to the method.
   *
   */
  private applyFlagModelToggle(state: boolean): boolean {
    this.isShown = state;
    return this.isShown;
  }
}
